import { inject, Injectable } from '@angular/core';
import { App } from '@df/core/app';
import { RequestService } from '@df/api/request/request.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { Klaviyo } from './klaviyo.interfaces';

/**
 * Klaviyo tracking service for sending custom events
 * Uses Klaviyo's client-side JS API
 */
@Injectable({
    providedIn: 'root'
})
export class KlaviyoTrackingService {
    /**
     * App service
     */
    private app = inject(App);

    /**
     * Request service for loading external scripts
     */
    private request = inject(RequestService);

    /**
     * Flag to track if script is loaded or loading
     */
    private scriptLoaded = false;
    /**
     * Promise to track if script is loaded or loading
     */
    private scriptPromise?: Promise<HTMLScriptElement>;
    /**
     * Klaviyo public API key
     */
    private readonly publicApiKey = this.app.dfConfig.klaviyo?.publicKey;

    constructor() {
        this.initialize();
    }

    /**
     * Identify a user in Klaviyo
     * @param properties User properties including email or phone_number (at least one required)
     * @param callback Optional callback function to execute after identification
     * @returns true if identification was initiated, false otherwise
     */
    async identify(properties: Klaviyo.IProfileProperties, callback?: Klaviyo.Callback): Promise<boolean> {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        // Ensure script is loaded
        if (!(await this.initialize())) {
            return false;
        }

        try {
            // Use klaviyo object if available
            if (window.klaviyo) {
                window.klaviyo.identify(properties, callback);
                return true;
            }

            // Fallback to _learnq queue
            if (!window._learnq) {
                window._learnq = [];
            }
            window._learnq.push(['identify', properties]);

            if (callback) {
                callback();
            }

            return true;
        } catch (error) {
            console.error('[Klaviyo] Error identifying user:', error);
            return false;
        }
    }

    /**
     * Track a custom event in Klaviyo
     * @param eventName The name of the event to track
     * @param properties Additional properties to send with the event
     * @param callback Optional callback function to execute after tracking
     * @returns true if tracking was initiated, false otherwise
     */
    async track(eventName: string, properties?: Klaviyo.IEventProperties, callback?: Klaviyo.Callback): Promise<boolean> {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        // Ensure script is loaded
        if (!(await this.initialize())) {
            return false;
        }

        try {
            // Use klaviyo object if available
            if (window.klaviyo) {
                window.klaviyo.track(eventName, properties || {}, callback);
                return true;
            }

            // Fallback to _learnq queue
            if (!window._learnq) {
                window._learnq = [];
            }
            window._learnq.push(['track', eventName, properties || {}]);

            if (callback) {
                callback();
            }

            return true;
        } catch (error) {
            console.error('[Klaviyo] Error tracking event:', error);
            return false;
        }
    }

    /**
     * Initialize Klaviyo by loading the tracking script
     * This method is idempotent - it will only load the script once
     */
    private async initialize(): Promise<boolean> {
        if (!this.app.isPlatformBrowser) {
            return false;
        }

        if (this.scriptLoaded) {
            return true;
        }

        if (!this.scriptPromise) {
            if (!this.publicApiKey) {
                console.error('[Klaviyo] Cannot initialize: PUBLIC_API_KEY not configured');
                return false;
            }

            // create promise to track if script is loaded or loading
            this.scriptPromise = this.request.loadScriptAsync(`https://static.klaviyo.com/onsite/js/${this.publicApiKey}/klaviyo.js`);

            try {
                await this.scriptPromise;
                this.scriptLoaded = true;
            } catch (error) {
                this.scriptLoaded = false;
                console.error('[Klaviyo] Failed to load script', error);
            }
        }

        return this.scriptLoaded;
    }
}
